import React from 'react';
import type { ReactNode } from 'react';
import { Link } from '@inertiajs/react';
import { useWikiShell } from './shell';

// 文档区的顶条：面包屑 + 右侧动作。
//
// 设计稿的顶条是**透明贴顶**的，滚动时不投影，只靠内容让它自然分层。
// 所以这里是 sticky + 与文档区同底色，没有 shadow、没有 backdrop-blur ——
// 那两样都会在一篇长文上面画出一条不该有的横线。
//
// 它由**页面组件**渲染而不是 WikiLayout：面包屑的第二段和右侧那几个按钮是
// 每一页自己的东西（正文页是「编辑 / 历史 / 删除」，历史页是「显示自动保存」），
// 而 Inertia 的 layout 函数拿不到页面的 props。
//
// 那两个与页面无关的按钮（收起边栏、窄屏打开抽屉）走 context —— 见 shell.ts。
type Crumb = { label: string; href?: string };

type TopBarProps = {
  // 「wiki」那一段是固定的，不用传。传的是它后面的层级，最后一段是当前位置。
  trail?: Crumb[];
  actions?: ReactNode;
};

const ICON_BTN =
  'grid h-[26px] w-[26px] shrink-0 place-items-center rounded-md text-[14px] ' +
  'text-todo-muted hover:bg-wiki-sideHover hover:text-todo-ink2';

const TopBar: React.FC<TopBarProps> = ({ trail = [], actions }) => {
  const { collapsed, toggleCollapsed, openDrawer } = useWikiShell();

  return (
    <div
      className="sticky top-0 z-10 flex h-[45px] items-center gap-2.5 bg-wiki-doc px-3.5"
    >
      {/* 窄屏：打开抽屉。 */}
      <button type="button" onClick={openDrawer} className={`${ICON_BTN} sm:hidden`} title="打开边栏">
        ☰
      </button>

      {/* 宽屏：只有**收起时**才在这里放一个 `»`。
          展开状态下的 `«` 在边栏的品牌行里（设计稿的位置），而它随边栏一起
          消失正是对的 —— 于是任何时刻「切换边栏」都恰好只有一个可见控件，
          不会出现展开时两个按钮做同一件事。 */}
      {collapsed && (
        <button
          type="button"
          onClick={toggleCollapsed}
          className={`${ICON_BTN} hidden sm:grid`}
          title="展开边栏"
        >
          »
        </button>
      )}

      <div className="flex min-w-0 items-center gap-1.5 text-[13px] text-todo-ink2">
        {/* 没有页面树，所以面包屑只有两级 —— 但它给的是「我在哪」这个信号，
            两级也成立。 */}
        <Link href="/wiki" className="shrink-0 hover:text-todo-accent">
          wiki
        </Link>
        {trail.map((crumb, index) => (
          <React.Fragment key={`${crumb.label}-${index}`}>
            <span className="shrink-0 text-todo-muted">/</span>
            {crumb.href ? (
              <Link href={crumb.href} className="max-w-[38ch] truncate hover:text-todo-accent">
                {crumb.label}
              </Link>
            ) : (
              // 最后一段是当前位置，不是链接。font-medium 而不是 bold：
              // 面包屑是个位置指示，不是标题。
              <span className="max-w-[38ch] truncate font-medium text-todo-ink">
                {crumb.label}
              </span>
            )}
          </React.Fragment>
        ))}
      </div>

      {actions && <div className="ml-auto flex shrink-0 items-center gap-0.5">{actions}</div>}
    </div>
  );
};

// 顶条上的按钮。三个变体，避免每个调用方各写一串类名（四个页面加起来有十来个
// 这样的按钮，写散了当天就开始漂）。
export const TBTN =
  'h-[26px] shrink-0 rounded-md px-2 text-[12.5px] text-todo-ink2 ' +
  'hover:bg-wiki-sideHover disabled:opacity-50';
export const TBTN_PRIMARY =
  'h-[26px] shrink-0 rounded-md bg-todo-accent px-2.5 text-[12.5px] text-white ' +
  'hover:bg-blue-600 disabled:opacity-50';
export const TBTN_DANGER =
  'h-[26px] shrink-0 rounded-md px-2 text-[12.5px] text-todo-ink2 ' +
  'hover:bg-wiki-sideHover hover:text-todo-urgent disabled:opacity-50';

export default TopBar;
